import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, MapPin, CalendarDays, LocateFixed, Ticket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/site/SiteBits";
import { usePageMeta } from "@/lib/site";
import { useAuth } from "@/context/AuthContext";
import { eventsApi, EVENT_CATEGORIES, pence, venueTime } from "@/lib/eventsApi";

export function EventCard({ e, wide = false }) {
  return (
    <Link to={`/events/${e.id}`} className={`group block overflow-hidden rounded-2xl border border-border bg-card card-hover ${wide ? "min-w-[280px] max-w-[300px] shrink-0" : ""}`} data-testid={`event-${e.id}`}>
      <div className="relative aspect-[16/9] overflow-hidden bg-muted">
        {e.cover_url && <img src={e.cover_url} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />}
        <span className="absolute left-3 top-3 rounded-full bg-background/90 px-2.5 py-1 text-[11px] font-semibold capitalize">{e.category}</span>
        {e.sold_out && <span className="absolute right-3 top-3 rounded-full bg-foreground px-2.5 py-1 text-[11px] font-semibold text-background">Sold out</span>}
        <span className="absolute bottom-3 left-3 rounded-full bg-background/90 px-3 py-1 text-sm font-semibold">{e.free ? "Free" : `From ${pence(e.from_p)}`}</span>
      </div>
      <div className="p-3.5">
        <p className="text-xs font-medium text-primary">{venueTime(e.starts_at)}</p>
        <h3 className="mt-1 truncate font-semibold text-[15px] leading-tight">{e.title}</h3>
        <p className="mt-1 flex items-center gap-1 truncate text-xs text-muted-foreground"><MapPin size={12} /> {e.online ? "Online" : [e.venue_name, e.city].filter(Boolean).join(" · ")}</p>
      </div>
    </Link>
  );
}

function Row({ title, items }) {
  if (!items?.length) return null;
  return (
    <section className="mt-8">
      <h2 className="font-serif-display text-2xl font-bold">{title}</h2>
      <div className="mt-4 flex gap-4 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {items.map((e) => <EventCard key={e.id} e={e} wide />)}
      </div>
    </section>
  );
}

export default function Events() {
  usePageMeta("Events", "Gigs, comedy, workshops, food markets and club nights near you — tickets on Fable +.");
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "";
  const q = params.get("q") || "";
  const [search, setSearch] = useState(q);
  const [pos, setPos] = useState(null);
  const [data, setData] = useState(undefined);

  useEffect(() => {
    let alive = true;
    setData(undefined);
    eventsApi.discover({ category: category || undefined, q: q || undefined, lat: pos?.lat, lng: pos?.lng })
      .then((d) => alive && setData(d))
      .catch(() => alive && setData(null));
    return () => { alive = false; };
  }, [category, q, pos, user?.id]);

  const set = (patch) => {
    const n = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? n.set(k, v) : n.delete(k)));
    setParams(n, { replace: true });
  };
  const nearMe = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((p) => setPos({ lat: p.coords.latitude, lng: p.coords.longitude }), () => setPos(null), { timeout: 8000 });
  };
  const rails = useMemo(() => data?.rails || [], [data]);
  const results = data?.results || [];
  const empty = data && !results.length && !rails.some((r) => r.items?.length);

  return (
    <div>
      <PageHeader title="What's on." lede="Gigs, comedy, workshops, food markets and club nights — real tickets, no hidden fees at the door.">
        <form onSubmit={(e) => { e.preventDefault(); set({ q: search.trim() }); }} className="flex max-w-lg items-center gap-2" role="search">
          <label className="sr-only" htmlFor="events-q">Search events</label>
          <div className="relative flex-1">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input id="events-q" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search events" data-testid="events-search" className="w-full rounded-full border border-border bg-card py-2.5 pl-9 pr-4 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
          </div>
          <Button type="submit" className="rounded-full px-5">Search</Button>
        </form>
      </PageHeader>
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={pos ? () => setPos(null) : nearMe} aria-pressed={!!pos} data-testid="events-near-me" className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium ${pos ? "border-transparent bg-foreground text-background" : "border-border bg-card hover:bg-muted"}`}>
            <LocateFixed size={14} /> Near me
          </button>
          {EVENT_CATEGORIES.map((c) => (
            <button key={c.key} type="button" onClick={() => set({ category: c.key })} aria-pressed={category === c.key} data-testid={`events-cat-${c.key || "all"}`} className={`rounded-full border px-3 py-1.5 text-sm font-medium ${category === c.key ? "border-transparent bg-foreground text-background" : "border-border bg-card hover:bg-muted"}`}>{c.label}</button>
          ))}
          {user && <Link to="/tickets" className="ml-auto inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"><Ticket size={14} /> My tickets</Link>}
        </div>

        {data === undefined ? (
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-64 rounded-2xl" />)}</div>
        ) : data === null ? (
          <p className="mt-10 text-center text-muted-foreground">Couldn't load events right now. Please try again shortly.</p>
        ) : empty ? (
          <div className="mt-10 rounded-2xl border border-dashed border-border p-10 text-center">
            <CalendarDays size={24} className="mx-auto text-muted-foreground" />
            <p className="mt-2 font-semibold">Nothing on for that yet</p>
            <p className="mt-1 text-sm text-muted-foreground">Try another category or clear the search.</p>
            <Button variant="outline" className="mt-4 rounded-full" onClick={() => { setSearch(""); setParams({}, { replace: true }); setPos(null); }}>Clear filters</Button>
          </div>
        ) : q ? (
          <div className="mt-8">
            <p className="text-sm text-muted-foreground">{results.length} result{results.length === 1 ? "" : "s"} for <span className="font-semibold text-foreground">{q}</span></p>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="events-results">{results.map((e) => <EventCard key={e.id} e={e} />)}</div>
          </div>
        ) : (
          <div data-testid="events-rails">
            {rails.map((r) => <Row key={r.key} title={r.title} items={r.items} />)}
          </div>
        )}
      </div>
    </div>
  );
}
